import { fetchCanonicalAttachmentBlob } from '../../../messaging/canonicalMessagingApi';
import { downloadFilename, mimeForFilename } from '../../views/chat/attachmentFilename';
import { canDownloadMedia } from './messageActionModel';

export const CHAT_MEDIA_DRAG_TYPE = 'application/x-srm-chat-media';
let currentDrag = null;
export const activeChatMediaDrag = () => currentDrag;
export const endChatMediaDrag = () => { currentDrag = null; };
export function chatMediaDragPayload(item, conversationId, quarantine) {
  if (quarantine?.status === 'quarantined' || !item.content?.attachment?.id || !['image', 'video', 'audio', 'document'].includes(item.kind) || !canDownloadMedia(item)) return null;
  return { conversationId, messageId: item.id, attachmentId: item.content.attachment.id,
    mime: item.content.attachment.mime_type || '', filename: item.content.attachment.file_name || '' };
}
export function beginChatMediaDrag(event, payload) {
  if (!payload) { event.preventDefault(); return; }
  event.stopPropagation();
  event.dataTransfer.clearData();
  event.dataTransfer.setData(CHAT_MEDIA_DRAG_TYPE, JSON.stringify(payload));
  event.dataTransfer.effectAllowed = 'copy';
  currentDrag = payload;
  const image = event.currentTarget.querySelector('img') || event.currentTarget.querySelector('.canonical-message-content svg');
  if (image && event.dataTransfer.setDragImage) {
    const preview = image.cloneNode(true);
    Object.assign(preview.style, { position: 'fixed', left: '-10000px', top: '0', width: '64px', height: '64px', maxWidth: '64px', objectFit: 'contain' });
    document.body.appendChild(preview);
    event.dataTransfer.setDragImage(preview, 12, 12);
    setTimeout(() => preview.remove(), 0);
  }
}
export function isChatMediaDrop(transfer) {
  const types = Array.from(transfer?.types || []);
  return types.includes(CHAT_MEDIA_DRAG_TYPE) || types.includes('Files') || types.includes('text/uri-list')
    || Boolean(transfer?.files?.length) || Array.from(transfer?.items || []).some(item => item.kind === 'file');
}
// External browser/app image drags can offer a URL instead of a FileList.
// Use browser CORS rules; never attach SRM credentials to a remote image fetch.
export async function fileFromExternalImageDrag(transfer) {
  const uri = (transfer.getData('text/uri-list') || '').split(/\r?\n/).map(line => line.trim()).find(line => line && !line.startsWith('#'));
  const url = new URL(uri);
  if (!['https:', 'http:', 'blob:', 'data:'].includes(url.protocol)) throw new Error('Unsupported image URL');
  const response = await fetch(url.href, { credentials: 'omit', referrerPolicy: 'no-referrer' });
  if (!response.ok) throw new Error('Image could not be loaded');
  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) throw new Error('Not an image');
  const extension = blob.type.split('/')[1].split('+')[0].replace(/[^a-z0-9]/gi, '') || 'jpg';
  const name = ['http:', 'https:'].includes(url.protocol) ? decodeURIComponent(url.pathname.split('/').pop() || '') : '';
  return new File([blob], name || `dropped-image.${extension}`, { type: blob.type });
}
export function readChatMediaDrag(transfer) {
  const data = JSON.parse(transfer.getData(CHAT_MEDIA_DRAG_TYPE));
  if (!data || ['conversationId', 'messageId', 'attachmentId'].some(key => typeof data[key] !== 'string' || !data[key])) throw new Error('Invalid media drag');
  return { conversationId: data.conversationId, messageId: data.messageId, attachmentId: data.attachmentId,
    mime: typeof data.mime === 'string' ? data.mime : '', filename: typeof data.filename === 'string' ? data.filename : '' };
}
export async function fileFromChatMediaDrag(payload) {
  const { blob, contentDisposition } = await fetchCanonicalAttachmentBlob(payload.attachmentId, { withMetadata: true });
  const specificMime = value => value && value !== 'application/octet-stream' ? value : '';
  let mime = specificMime(blob.type) || specificMime(payload.mime);
  const kind = mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : mime.startsWith('audio/') ? 'audio' : 'document';
  const name = await downloadFilename({ id: payload.messageId, kind, content: { attachment: { file_name: payload.filename, mime_type: mime } } }, blob, contentDisposition);
  mime = mime || mimeForFilename(name);
  return new File([blob], name, { type: mime });
}
